import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Typography,
  Box,
} from "@mui/material";

const fieldSx = {
  mb: 2,
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
};

/**
 * Add / edit a recurring commemorative date on a client.
 */
export default function CustomDateDialog({
  open,
  onClose,
  clients,
  initial,
  onSave,
  saving,
}) {
  const [userId, setUserId] = useState(initial?.user_id || "");
  const [label, setLabel] = useState(initial?.label || "");
  const [month, setMonth] = useState(initial?.month || "");
  const [day, setDay] = useState(initial?.day || "");
  const [enabled, setEnabled] = useState(initial?.enabled !== false);

  React.useEffect(() => {
    if (!open) return;
    setUserId(initial?.user_id || "");
    setLabel(initial?.label || initial?.detail || "");
    setMonth(initial?.month || "");
    setDay(initial?.day || "");
    setEnabled(initial?.enabled !== false);
  }, [open, initial]);

  const selected = (clients || []).find((c) => c.id === userId) || null;

  const handleSave = () => {
    const m = Number(month);
    const d = Number(day);
    if (!userId) return;
    if (!label.trim()) return;
    if (!m || m < 1 || m > 12 || !d || d < 1 || d > 31) return;
    onSave?.({
      user_id: userId,
      id: initial?.custom_id || initial?.id,
      label: label.trim(),
      month: m,
      day: d,
      enabled,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: "#1a1a2e", color: "#fff" } }}
    >
      <DialogTitle>{initial?.custom_id || initial?.id ? "Edit custom date" : "Add custom date"}</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 13, mb: 2 }}>
          Recurring yearly date for an individual client (e.g. wedding anniversary). Stored on the
          client profile.
        </Typography>
        <Autocomplete
          options={clients || []}
          getOptionLabel={(o) => `${o.full_name || "—"} (${o.email || ""})`}
          value={selected}
          onChange={(_, v) => setUserId(v?.id || "")}
          disabled={Boolean(initial?.user_id)}
          renderInput={(params) => (
            <TextField {...params} label="Client" size="small" sx={fieldSx} />
          )}
          sx={{
            mb: 1,
            "& .MuiAutocomplete-popupIndicator": { color: "rgba(255,255,255,0.5)" },
          }}
        />
        <TextField
          fullWidth
          size="small"
          label="Label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={fieldSx}
        />
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            inputProps={{ min: 1, max: 12 }}
            sx={{ ...fieldSx, width: 120 }}
          />
          <TextField
            size="small"
            type="number"
            label="Day"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            inputProps={{ min: 1, max: 31 }}
            sx={{ ...fieldSx, width: 120 }}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#2af598" } }}
            />
          }
          label={<Typography sx={{ color: "#fff" }}>Enabled</Typography>}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={handleSave}
          sx={{ bgcolor: "#2af598", color: "#0a1a12" }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
